import { useCallback, useState } from 'react'
import { notificationRepository } from '@/data/notifications/repositories/notification_repository_impl'
import type { NotificationListItem } from '@/domain/notifications/entities/notification_list_item.entity'
import { DeleteNotificationUseCase } from '@/domain/notifications/usecases/delete_notification.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import { matchNotification } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'

const deleteNotification = new DeleteNotificationUseCase(notificationRepository)

export function useNotificationsListViewModel() {
  const [notifications, setNotifications] = useState<NotificationListItem[]>([])
  const isLoading = false
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    notificationId: null as string | null,
    notificationTitle: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)

  const { table, actions: tableActions } = useTableQueryState({
    items: notifications,
    matchesSearch: matchNotification,
  })

  const openDeleteDialog = useCallback((notificationId: string, notificationTitle: string) => {
    setDeleteDialog({ isOpen: true, notificationId, notificationTitle })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ isOpen: false, notificationId: null, notificationTitle: '' })
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.notificationId) return
    try {
      setIsDeleting(true)
      await deleteNotification.execute(deleteDialog.notificationId)
      setNotifications(prev => prev.filter(n => n.id !== deleteDialog.notificationId))
      closeDeleteDialog()
    } catch (error: unknown) {
      logError('Failed to delete notification:', error)
      alert(
        getAxiosLikeErrorMessage(error, 'Failed to delete notification. Please try again.')
      )
    } finally {
      setIsDeleting(false)
    }
  }, [deleteDialog.notificationId, closeDeleteDialog])

  return {
    state: {
      notifications,
      filteredNotifications: table.filteredItems,
      paginatedNotifications: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    isDeleting,
    actions: {
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort: tableActions.handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
